function renderTableClientModule({
  plural,
  schemaName,
  singular,
  actionImportPath,
  filterProps,
  filterParams,
  camelPlural,
  schemaLabel,
  hasEdit,
  adminRoutePath
}: {
  plural: string
  schemaName: string
  singular: string
  actionImportPath: string
  filterProps: string
  filterParams: string
  camelPlural: string
  schemaLabel: string
  hasEdit: boolean
  adminRoutePath: string
}): string {
  return `'use client'

import {
  DataTablePagination,
} from '@admin/components/shared/data-table/data-table-pagination'
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHead,
  DataGridHeader,
  DataGridRow
} from '@admin/components/shared/data-table/data-grid'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import { GripVertical } from 'lucide-react'
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
${hasEdit ? "import { useRouter } from 'next/navigation'\n" : ''}import { use${plural} } from '@admin/hooks/use-${schemaName}'
import type { ${singular} } from '@admin/actions/${actionImportPath}'
import { useTableUtils } from '@admin/hooks/use-table-utils'
import { cn } from '@admin/utils/shared/cn'

interface ${plural}TableProps<TValue> extends React.HTMLAttributes<HTMLDivElement> {
  columns: ColumnDef<${singular}, TValue>[]
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  search?: string
  ${filterProps}
}

export function ${plural}Table<TValue>({ columns, selectedIds, setSelectedIds, search, ${filterParams}, className }: ${plural}TableProps<TValue>) {
  const defaultPageSize = 20
${hasEdit ? '  const router = useRouter()\n' : ''}  const [{
    page: pageIndex,
    size: pageSize,
    sort: sortField,
    dir: sortDir
  }, setTableQuery] = useQueryStates({
    page: parseAsInteger.withDefault(0),
    size: parseAsInteger.withDefault(defaultPageSize),
    sort: parseAsString.withDefault(''),
    dir: parseAsString.withDefault('')
  })

  const safePageIndex = Number.isFinite(pageIndex) && pageIndex > 0 ? pageIndex : 0
  const safePageSize = pageSize === -1 || (Number.isFinite(pageSize) && pageSize > 0) ? pageSize : defaultPageSize
  const { data, error, isPending } = use${plural}(search, ${filterParams}, safePageIndex, safePageSize, sortField || undefined, (sortDir as 'asc' | 'desc') || undefined)

  const tableData = data?.${camelPlural} ?? []
  const totalRows = data?.total ?? 0
  const effectivePageSize = safePageSize === -1 ? totalRows : safePageSize
  const pageCount = effectivePageSize > 0 ? Math.ceil(totalRows / effectivePageSize) : 1
  const shouldShowPagination = totalRows > defaultPageSize

  const {
    columnSizing,
    handleColumnSizingChange,
    handlePaginationChange,
    handleRowSelectionChange,
    handleSortingChange,
    hoveredColumnId,
    registerTable,
    resetColumnSizingToContainer,
    rowSelection,
    setHoveredColumnId,
    sorting,
    tableContainerRef
  } = useTableUtils<${singular}>({
    data: tableData,
    selectedIds,
    onSelectedIdsChange: setSelectedIds,
    pageIndex: safePageIndex,
    pageSize: effectivePageSize,
    setPageIndex: (nextPageIndex) => setTableQuery({ page: nextPageIndex }),
    setSortingParams: ({
      pageIndex: nextPageIndex,
      sortDir: nextSortDir,
      sortField: nextSortField
    }) => setTableQuery({
      page: nextPageIndex,
      sort: nextSortField,
      dir: nextSortDir
    }),
    sortField,
    sortDir
  })

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 48,
      size: 220
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    rowCount: totalRows,
    onSortingChange: handleSortingChange,
    onColumnSizingChange: handleColumnSizingChange,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      columnSizing,
      rowSelection,
      pagination: {
        pageIndex: safePageIndex,
        pageSize: effectivePageSize
      }
    }
  })
  registerTable(table)
  const gridTemplateColumns = table
    .getVisibleLeafColumns()
    .map((column) => \`\${column.getSize()}px\`)
    .join(' ')

  return (
    <div
      className={cn("flex flex-col gap-6 pt-px pb-2 h-full", className)}
    >
      <DataGrid containerClassName="min-h-0 flex-1" containerRef={tableContainerRef} gridTemplateColumns={gridTemplateColumns} style={{ width: table.getTotalSize() }}>
        <DataGridHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <DataGridRow key={headerGroup.id}>
              {headerGroup.headers.map((header, headerIndex) => {
                const meta = header.column.columnDef.meta as Record<string, unknown> | undefined
                const resizeLabel = typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : header.column.id
                const nextHeader = headerGroup.headers[headerIndex + 1]
                const canResizeColumn = header.column.getCanResize() && nextHeader?.column.getCanResize() === true
                return (
                  <DataGridHead
                    key={header.id}
                    className={cn('group/resize-header relative overflow-hidden', {
                      'text-center': meta?.align === 'center',
                      'text-right': meta?.align === 'right'
                    })}
                    data-column-id={header.column.id}
                    onMouseEnter={() => setHoveredColumnId(header.column.id)}
                    onMouseLeave={() => setHoveredColumnId((current) => (current === header.column.id ? null : current))}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {canResizeColumn ? (
                      <button
                        type="button"
                        aria-label={\`Resize \${resizeLabel} column\`}
                        className="group/resize-handle absolute right-0 top-0 z-10 flex h-full w-5 cursor-col-resize touch-none select-none items-center justify-center text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:text-foreground focus-visible:opacity-100 focus-visible:outline-none group-focus-within/resize-header:opacity-100 data-[column-hovered=true]:opacity-100 data-[resizing=true]:opacity-100"
                        data-resize-handle
                        data-column-hovered={hoveredColumnId === header.column.id ? 'true' : 'false'}
                        data-resizing={header.column.getIsResizing() ? 'true' : 'false'}
                        onDoubleClick={resetColumnSizingToContainer}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                      >
                        <GripVertical aria-hidden="true" className={cn('size-3.5', { 'text-ring': header.column.getIsResizing() })} />
                      </button>
                    ) : null}
                  </DataGridHead>
                )
              })}
            </DataGridRow>
          ))}
        </DataGridHeader>
        <DataGridBody>
          {isPending ? (
            <DataGridRow>
              <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
                <div className="text-muted-foreground">Loading ${schemaLabel}…</div>
              </DataGridCell>
            </DataGridRow>
          ) : error ? (
            <DataGridRow>
              <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
                <div className="text-destructive">Error loading ${schemaLabel}: {error.message}</div>
              </DataGridCell>
            </DataGridRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <DataGridRow
                key={row.id}
                className={cn('hover:bg-muted/50', {
                  'cursor-pointer': ${hasEdit}
                })}
                data-state={row.getIsSelected() ? 'selected' : undefined}${hasEdit ? renderTableRowClickHandler({ editRoutePath: `${adminRoutePath}/${schemaName}` }) : ''}
              >
                {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as Record<string, unknown> | undefined
                return (
                    <DataGridCell
                      key={cell.id}
                      className={cn({
                        'text-center': meta?.align === 'center',
                        'text-right': meta?.align === 'right'
                      })}
                      data-column-id={cell.column.id}
                      onMouseEnter={() => setHoveredColumnId(cell.column.id)}
                      onMouseLeave={() => setHoveredColumnId((current) => (current === cell.column.id ? null : current))}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </DataGridCell>
                  )
                })}
              </DataGridRow>
            ))
          ) : (
            <DataGridRow>
              <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
                No ${schemaLabel} found.
              </DataGridCell>
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>

      {shouldShowPagination ? (
        <DataTablePagination
          table={table}
          paginationMode="controlled"
          pageSize={safePageSize}
          pageIndex={safePageIndex}
          pageCount={pageCount}
          setPageIndex={(nextPageIndex) => setTableQuery({ page: nextPageIndex })}
          setPageSize={(nextPageSize) =>
            setTableQuery({ page: 0, size: nextPageSize })
          }
        />
      ) : null}
    </div>
  )
}
`
}

function renderTableRowClickHandler({ editRoutePath }: { editRoutePath: string }): string {
  return `
                onClick={(e) => {
                  const target = e.target as HTMLElement
                  if (!e.currentTarget.contains(target)) return
                  if (target.closest('button, a, input, [role="checkbox"]')) return
                  router.push(\`${editRoutePath}/\${row.original.id}/edit\`)
                }}`
}

export function renderTableRenderer(
  request:
    | {
        kind: 'renderTableClientModule'
        props: {
          plural: string
          schemaName: string
          singular: string
          actionImportPath: string
          filterProps: string
          filterParams: string
          camelPlural: string
          schemaLabel: string
          hasEdit: boolean
          adminRoutePath: string
        }
      }
    | { kind: 'renderTableRowClickHandler'; props: { editRoutePath: string } }
): string {
  switch (request.kind) {
    case 'renderTableClientModule':
      return renderTableClientModule(request.props)
    case 'renderTableRowClickHandler':
      return renderTableRowClickHandler(request.props)
  }

  throw new Error('Unsupported renderer request')
}
